/**
 * Phase 9.6 Batch 2 add-on — 🏆 Featured Board
 * ---------------------------------------------
 * Official "who's featured right now" board for the Discover → Featured tab.
 *
 * Data source: GET /api/submissions/featured?limit=50&sort=newest
 *   - Returns ALL tracks with status='featured', newest first.
 *   - Enriched with user_badges, profile photo, gift counts, reactions.
 *
 * Refreshes:
 *   - On mount
 *   - Soft-refresh every 60s while the tab is visible (no manual refresh
 *     needed when new tracks get featured by admin)
 *
 * Includes a permanent "How to Get Featured" info card at the top so the
 * board doubles as both a flex and a goal.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getDisplayPlays, getMomentumBadge } from "@/lib/promoPlays";
import { FeaturedNowBanner } from "@/components/FeaturedCelebration";
import FeatureBoostChip from "@/components/FeatureBoostChip";

const REFRESH_MS = 60_000;

const fmt = (n) => Number(n || 0).toLocaleString();

const FeaturedBoard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const timerRef = useRef(null);

  const load = useCallback(async (mode = sortBy, soft = false) => {
    if (!soft) setLoading(true);
    try {
      const r = await api.get(`/submissions/featured?limit=50&sort=${mode}`);
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setItems(arr);
    } catch (_e) {
      if (!soft) setItems([]);
    } finally {
      if (!soft) setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    load(sortBy);
    timerRef.current = setInterval(() => load(sortBy, true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [sortBy, load]);

  return (
    <section data-testid="featured-board-section">
      {/* How to Get Featured info card */}
      <Card
        className="p-5 mb-6 bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-pink-500/10 border-amber-400/40"
        data-testid="how-to-get-featured-card"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="text-4xl shrink-0">🎖️</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-black text-white">How to Get Featured</h3>
            <p className="text-sm text-amber-100/85 mt-1 leading-relaxed">
              Drop your strongest track, earn fan reactions, climb the weekly
              Spotlight, and the team will hand-pick the standouts for the
              official Feature Board. Featured artists unlock the 🎖️ badge
              and ride this board's recency boost.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Link to="/how-to-get-featured" data-testid="how-to-get-featured-link">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white text-xs font-bold"
                >
                  📖 Full Guide →
                </Button>
              </Link>
              <Link to="/submit">
                <Button size="sm" variant="outline" className="border-amber-400/40 text-amber-200 hover:bg-amber-500/10 text-xs">
                  🎤 Submit a Track
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-2">
            <span>🏆</span> The Feature Board
          </h2>
          <p className="text-sm text-amber-200/70 mt-1">
            Every track currently rocking the 🎖️ Featured badge.
          </p>
        </div>
        <div
          className="flex items-center gap-1 bg-black/40 border border-amber-500/30 rounded-full p-1"
          data-testid="featured-board-sort"
        >
          {[
            { id: "newest", label: "🆕 Newest" },
            { id: "momentum", label: "🔥 Momentum" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setSortBy(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                sortBy === s.id
                  ? "bg-gradient-to-r from-amber-500 to-pink-500 text-white shadow"
                  : "text-amber-200/70 hover:text-white"
              }`}
              data-testid={`featured-board-sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <p className="text-amber-200/60 text-sm text-center py-12" data-testid="featured-board-loading">
          Loading the Feature Board…
        </p>
      )}

      {!loading && items.length === 0 && (
        <Card className="p-12 text-center bg-black/40 border-amber-500/20" data-testid="featured-board-empty">
          <p className="text-5xl mb-3">🎖️</p>
          <p className="text-white font-bold text-lg">No featured tracks yet.</p>
          <p className="text-amber-200/70 text-sm mt-1">Be the first — submit a track and chase the spotlight.</p>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-board-grid">
          {items.map((t) => (
            <Link
              key={t.submission_id}
              to={`/track/${t.submission_id}`}
              className="group relative bg-gradient-to-br from-amber-500/10 via-black/60 to-pink-500/10 rounded-xl border border-amber-500/30 hover:border-amber-400/70 overflow-hidden transition-all shadow-lg shadow-amber-500/10"
              data-testid={`featured-board-card-${t.submission_id}`}
            >
              {/* Cover */}
              <div className="relative aspect-video bg-black overflow-hidden">
                <img
                  src={getCoverUrl(t)}
                  alt={t.track_title || "Featured track"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=400&fit=crop"; }}
                />
                <span className="absolute top-2 left-2 px-2 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-[10px] font-black uppercase tracking-wider shadow-lg flex items-center gap-1">
                  🎖️ Featured
                </span>
                <FeaturedNowBanner
                  featuredAt={t.featured_at}
                  className="absolute bottom-2 left-2"
                />
                <FeatureBoostChip
                  submission={t}
                  variant="meter"
                  className="absolute bottom-2 right-2 shadow-lg"
                />
                {t.featured_at && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-amber-100 text-[9px] font-bold">
                    {new Date(t.featured_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
              {/* Body */}
              <div className="p-3">
                <p className="text-white font-bold text-sm truncate group-hover:text-amber-100">
                  {t.track_title || "Untitled"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <img
                    src={getProfilePhotoUrl({ profile_photo: t.user_profile_photo, picture: t.user_picture })}
                    alt=""
                    className="w-5 h-5 rounded-full object-cover shrink-0 border border-amber-400/30"
                  />
                  <p className="text-xs text-amber-200/80 truncate flex items-center gap-1">
                    {t.artist_name || "—"}
                    {t.verified && <span className="text-blue-400 text-[10px]">✓</span>}
                  </p>
                </div>
                {/* Signals row — Phase 11.0: unified `display_plays`
                    (real + capped promotional plays) drives the public
                    plays number. Real `play_count` is still on the doc
                    for debug, but never shown alone on emotional surfaces.
                    Fresh feature (<5 min) still gets a "gathering…" hint
                    so the milestone toast pipeline has time to fire. */}
                {(() => {
                  const ageMs = t.featured_at ? Date.now() - new Date(t.featured_at).getTime() : Infinity;
                  const veryFresh = ageMs >= 0 && ageMs < 5 * 60 * 1000;
                  const plays = getDisplayPlays(t);
                  const chip = getMomentumBadge(t) || "🚀 In Rotation";
                  return (
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-amber-200/70 flex-wrap">
                      <span
                        data-testid="momentum-chip"
                        className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-100 font-bold uppercase tracking-wider text-[9px]"
                      >
                        {chip}
                      </span>
                      {(t.gift_total_coins || 0) > 0 && (
                        <span title="Total coin gifts on this track">🪙 <span className="text-white font-bold">{fmt(t.gift_total_coins)}</span></span>
                      )}
                      {(t.reaction_count || t.fire_count || 0) > 0 && (
                        <span title="Fan 🔥 reactions">🔥 <span className="text-white font-bold">{fmt(t.reaction_count || t.fire_count)}</span></span>
                      )}
                      {veryFresh ? (
                        <span className="text-amber-300/65 italic" title="Momentum is gathering — full plays number lands in a moment">
                          gathering momentum…
                        </span>
                      ) : (
                        plays > 0 && (
                          <span className="opacity-80" title="Plays on this track (real + active feature push)">
                            ▶ <span className="text-amber-100/90 font-semibold">{fmt(plays)}</span>
                          </span>
                        )
                      )}
                    </div>
                  );
                })()}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default FeaturedBoard;
